import { BookOpen, Home, Users, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';
import { api } from '@/App';
import AppInstallBanner from '@/components/AppInstallBanner';

// Detect if user is in an embedded browser (WebView)
const isEmbeddedBrowser = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  
  // Check for common in-app browsers
  const isWebView = (
    // Facebook
    ua.includes('FBAN') || ua.includes('FBAV') ||
    // Instagram
    ua.includes('Instagram') ||
    // Twitter
    ua.includes('Twitter') ||
    // LinkedIn
    ua.includes('LinkedInApp') ||
    // Pinterest
    ua.includes('Pinterest') ||
    // Snapchat
    ua.includes('Snapchat') ||
    // TikTok
    ua.includes('BytedanceWebview') || ua.includes('TikTok') ||
    // Generic WebView detection
    ua.includes('wv') ||
    // iOS WebView
    (ua.includes('iPhone') && !ua.includes('Safari')) ||
    // Android WebView
    (ua.includes('Android') && ua.includes('; wv)'))
  );
  
  return isWebView;
};

const Landing = () => {
  const [appLogo, setAppLogo] = useState(null);
  const [appName, setAppName] = useState('');
  const [showWebViewWarning, setShowWebViewWarning] = useState(false);

  useEffect(() => {
    fetchAppLogo();
    // Check for embedded browser on mount
    if (isEmbeddedBrowser()) {
      setShowWebViewWarning(true);
    }
  }, []);

  const fetchAppLogo = async () => {
    try {
      const response = await api.get('/settings/logo');
      if (response.data.logo_url) {
        setAppLogo(response.data.logo_url);
      }
      if (response.data.app_name) {
        setAppName(response.data.app_name);
      }
    } catch (error) {
      console.error('Error fetching app logo:', error);
    }
  };

  const handleLogin = () => {
    if (isEmbeddedBrowser()) {
      setShowWebViewWarning(true);
      return;
    }
    const redirectUrl = `${window.location.origin}/dashboard`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLearnClick = () => {
    if (isEmbeddedBrowser()) {
      setShowWebViewWarning(true);
      return;
    }
    const redirectUrl = `${window.location.origin}/learn`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleCommunityClick = () => {
    if (isEmbeddedBrowser()) {
      setShowWebViewWarning(true);
      return;
    }
    const redirectUrl = `${window.location.origin}/community`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleCareTrainingClick = () => {
    if (isEmbeddedBrowser()) {
      setShowWebViewWarning(true);
      return;
    }
    const redirectUrl = `${window.location.origin}/dashboard`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleVirtualAdoptionClick = () => {
    if (isEmbeddedBrowser()) {
      setShowWebViewWarning(true);
      return;
    }
    const redirectUrl = `${window.location.origin}/virtual-corral`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const copyUrlToClipboard = () => {
    navigator.clipboard.writeText(window.location.href);
    alert('Link copied! Paste it in Safari or Chrome to continue.');
  };

  return (
    <div className="min-h-screen" style={{backgroundColor: '#FCFCF8'}}>
      {/* WebView Warning Modal */}
      {showWebViewWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <h2 className="text-xl font-bold text-gray-800 mb-3">
              📱 Open in Browser
            </h2>
            <p className="text-gray-600 mb-4">
              Google Sign-In doesn't work in this browser. Please open this page in <strong>Safari</strong> or <strong>Chrome</strong> to continue.
            </p>
            <div className="space-y-3">
              <Button 
                onClick={copyUrlToClipboard}
                className="w-full"
                style={{backgroundColor: '#6FCFD3'}}
              >
                Copy Link
              </Button>
              <p className="text-sm text-gray-500 text-center">
                Tap the <strong>⋮</strong> or <strong>⋯</strong> menu and select "Open in Safari/Chrome"
              </p>
              <button 
                onClick={() => setShowWebViewWarning(false)}
                className="w-full text-gray-500 text-sm underline"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* App Install Banner */}
      <AppInstallBanner />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center fade-in">
            <div className="mb-8 flex flex-col items-center">
              {appLogo ? (
                <img 
                  src={appLogo} 
                  alt={appName} 
                  className="w-80 h-auto object-contain mb-4"
                />
              ) : (
                <img 
                  src="https://customer-assets.emergentagent.com/job_ranch-social/artifacts/cprb8mpd_Logo%20cream.png" 
                  alt="Logo" 
                  className="w-80 h-auto object-contain"
                />
              )}
            </div>
            
            {/* Bible Verse */}
            <div className="mb-8 max-w-2xl mx-auto">
              <p className="text-base sm:text-lg italic font-serif mb-1" style={{color: '#6FCFD3'}}>
                "I will make a way in the wilderness and rivers in the desert."
              </p>
              <p className="text-sm font-semibold" style={{color: '#D66238'}}>
                - Isaiah 43:19
              </p>
            </div>
            
            <p className="text-lg sm:text-xl max-w-3xl mx-auto mb-8" style={{color: '#D66238', opacity: 0.8}}>
              Learn about American Mustangs and experience the journey of adoption through an engaging virtual simulator. Build empathy, knowledge, and skills for responsible horsemanship.
            </p>
            <Button
              data-testid="get-started-btn"
              onClick={handleLogin}
              className="px-8 py-6 text-lg font-semibold rounded-full"
              style={{backgroundColor: '#6FCFD3', color: '#FCFCF8'}}
            >
              Get Started
            </Button>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16" style={{color: '#D66238'}}>
          What You'll Experience
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          <FeatureCard
            icon={<img src="https://customer-assets.emergentagent.com/job_ranch-simulator-1/artifacts/achdemxb_AFF7D020-A832-42CA-AE92-A5A038BAD074.png" alt="Map" className="w-12 h-12 object-contain" />}
            title="Learn & Explore"
            description="Discover mustang history, BLM adoption process, and explore herds across the American West."
            onClick={handleLearnClick}
          />
          <FeatureCard
            icon={<img src="https://customer-assets.emergentagent.com/job_ranch-adventure-2/artifacts/jr5xfglu_11995520-A538-462A-BE24-F6468B106640.png" alt="Horse" className="w-12 h-12 object-contain" />}
            title="Virtual Adoption"
            description="Experience the real adoption process from facility qualification to selecting your mustang."
            onClick={handleVirtualAdoptionClick}
          />
          <FeatureCard
            icon={<img src="https://customer-assets.emergentagent.com/job_ranch-simulator-1/artifacts/6u83r2eq_F634D6F1-53B8-4075-BE12-2E515CC25E8F.png" alt="Care & Training" className="w-12 h-12 object-contain" />}
            title="Care & Training"
            description="Build trust through daily care, training mini-games, and watch your mustang progress."
            onClick={handleCareTrainingClick}
          />
          <FeatureCard
            icon={<img src="https://customer-assets.emergentagent.com/job_ranch-simulator-1/artifacts/vbuyzsha_stable_transparent.png" alt="Stable" className="w-12 h-12 object-contain" />}
            title="Community"
            description="Earn badges, share your progress, and connect with fellow mustang enthusiasts."
            onClick={handleCommunityClick}
          />
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20" style={{backgroundColor: '#6FCFD3'}}>
        <div className="max-w-4xl mx-auto text-center px-4">
          <Award className="w-16 h-16 mx-auto mb-6" style={{color: '#FCFCF8'}} />
          <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{color: '#FCFCF8'}}>
            Start Your Mustang Journey Today
          </h2>
          <p className="text-lg mb-8" style={{color: '#FCFCF8', opacity: 0.95}}>
            Join thousands learning about mustang preservation and responsible adoption.
          </p>
          <Button
            data-testid="cta-login-btn"
            onClick={handleLogin}
            className="px-8 py-6 text-lg font-semibold rounded-full"
            style={{backgroundColor: '#6FCFD3', color: '#FCFCF8'}}
          >
            Begin Your Adventure
          </Button>
        </div>
      </div>
    </div>
  );
};

const FeatureCard = ({ icon, title, description, onClick }) => {
  return (
    <div 
      className={`p-6 rounded-2xl transition-all duration-300 ${onClick ? 'cursor-pointer hover:shadow-xl hover:scale-105' : ''}`}
      style={{
        backgroundColor: '#fff', 
        border: onClick ? '2px solid #D66238' : '2px solid #6FCFD3',
        position: 'relative'
      }}
      onClick={onClick}
    >
      <div className="mb-4">{icon}</div>
      <h3 className="text-xl font-semibold mb-3" style={{color: '#D66238'}}>{title}</h3>
      <p style={{color: '#372F2F', opacity: 0.7}}>{description}</p>
      {onClick && (
        <div className="mt-4 flex items-center justify-center">
          <span className="text-sm font-semibold px-4 py-2 rounded-full" style={{backgroundColor: '#D66238', color: '#fff'}}>
            Explore Now →
          </span>
        </div>
      )}
    </div>
  );
};

export default Landing;